import { useState, useEffect } from 'react'
import useAppStore from '../stores/useAppStore'
import { loginService } from '../services/loginService'
import { authStorage } from '../utils/authStorage'
import { sweetAlert } from '../services/sweetAlertService'

/**
 * Hook personalizado para gestión de login
 * REFACTORIZADO siguiendo TODAS las reglas:
 *
 * ✅ Regla #2: Toda la lógica de login centralizada en este hook
 * ✅ Regla #5: Estados gestionados completamente por el hook
 * ✅ Regla #7: Integración con servicios especializados
 *
 * @returns {Object} Estados y funciones de login
 */
const useLogin = () => {
  // Estados del formulario
  const [username, setUsername] = useState('') // Mantener nombre por compatibilidad pero será usado para correo
  const [password, setPassword] = useState('')
  const [rememberMe, setRememberMe] = useState(false)
  const [showCredentials, setShowCredentials] = useState(false)
  const [showRegistrationForm, setShowRegistrationForm] = useState(false)
  const [showParticularService, setShowParticularService] = useState(false)

  // Estado global
  const { login, isLoading } = useAppStore()

  // Cargar credenciales guardadas al montar el componente
  useEffect(() => {
    const savedEmail = authStorage.getSavedEmail()
    if (savedEmail) {
      setUsername(savedEmail)
      setRememberMe(true)
    }
  }, [])

  /**
   * Maneja el envío del formulario de login
   */
  const handleSubmit = async (e) => {
    e.preventDefault()

    if (username && password) {
      try {
        const result = await login(username, password)

        if (result.success) {
          // Guardar credenciales si el usuario marcó "Recordar"
          authStorage.saveCredentials(username, rememberMe)
          sweetAlert.showWelcome(result.user?.nombres || result.user?.nombre_usuario)
        } else {
          sweetAlert.showLoginError(result.error || 'Error al iniciar sesión')
        }
      } catch (error) {
        sweetAlert.showError(error.message || 'Error de conexión con el servidor')
      }
    } else {
      sweetAlert.showWarning('Por favor ingresa tu nombre de usuario y contraseña')
    }
  }

  /**
   * Maneja el login rápido con credenciales predefinidas
   */
  const quickLogin = async (type) => {
    const credentials = loginService.getQuickLoginCredentials(type)

    try {
      const result = await login(credentials.username, credentials.password)

      if (!result.success) {
        sweetAlert.showError(result.error || 'Error en acceso rápido')
      } else {
        sweetAlert.showWelcome(result.user?.nombres || result.user?.nombre_usuario)
      }
    } catch (error) {
      sweetAlert.showError(error.message || 'Error en acceso rápido')
    }
  }

  /**
   * Alterna la visibilidad de las credenciales
   */
  const toggleCredentials = () => {
    setShowCredentials(!showCredentials)
  }

  /**
   * Navega al formulario de registro
   */
  const goToRegistration = () => {
    setShowRegistrationForm(true)
  }

  /**
   * Vuelve del formulario de registro
   */
  const backFromRegistration = () => {
    setShowRegistrationForm(false)
  }

  /**
   * Navega al servicio particular
   */
  const goToParticular = () => {
    setShowParticularService(true)
  }

  /**
   * Vuelve del servicio particular
   */
  const backFromParticular = () => {
    setShowParticularService(false)
  }

  return {
    // Estados del formulario
    username,
    password,
    rememberMe,
    showCredentials,
    showRegistrationForm,
    showParticularService,
    isLoading,

    // Funciones de cambio de estado
    setUsername,
    setPassword,
    setRememberMe,

    // Funciones de acción
    handleSubmit,
    quickLogin,
    toggleCredentials,

    // Funciones de navegación
    goToRegistration,
    backFromRegistration,
    goToParticular,
    backFromParticular
  }
}

export default useLogin
